import { useCallback, useEffect, useRef, useState } from 'react'
import { supplyRequest } from './api'
import { DeliveryAnalysis } from './model'
import { AnalysisState, savedAnalysisState, synchronizeAnalysis } from './analysis'

export function useDeliveryAnalyses(onReady: () => void) {
  const [analyses, setAnalyses] = useState<DeliveryAnalysis[]>([])
  const [states, setStates] = useState<Record<string, AnalysisState>>({})
  const [error, setError] = useState('')
  const statesRef = useRef(states)
  const readyCallback = useRef(onReady)
  const reloadRef = useRef<() => void>(() => {})
  readyCallback.current = onReady
  const refresh = useCallback(() => reloadRef.current(), [])
  const track = useCallback((row: DeliveryAnalysis) => {
    statesRef.current = { ...statesRef.current, [row.review_id]: { status: 'processing' } }
    setStates(statesRef.current)
    setAnalyses(previous => [row, ...previous.filter(item => item.review_id !== row.review_id)])
    reloadRef.current()
  }, [])
  useEffect(() => {
    let active = true,
      running = false,
      requested = false
    const refresh = async () => {
      if (!active || window.document.hidden) return
      if (running) {
        requested = true
        return
      }
      running = true
      try {
        const result = await supplyRequest('/api/supply/delivery-analyses', { timeoutMs: 20000 })
        if (!active) return
        const rows: DeliveryAnalysis[] = result.data || []
        setAnalyses(rows)
        setError('')
        const next = { ...statesRef.current }
        let changed = false
        const queue = rows.filter(
          row =>
            !row.processing_job_id &&
            !row.document_count &&
            (!next[row.review_id] ||
              ['processing', 'unavailable'].includes(next[row.review_id].status)),
        )
        rows
          .filter(row => row.document_count || row.processing_job_id)
          .forEach(row => {
            const saved = savedAnalysisState(row)
            if (next[row.review_id]?.status !== saved.status || next[row.review_id]?.documentCount !== saved.documentCount
              || next[row.review_id]?.checkedCount !== saved.checkedCount) changed = true
            next[row.review_id] = saved
          })
        const worker = async () => {
          while (active && queue.length) {
            const row = queue.shift()!
            try {
              next[row.review_id] = await synchronizeAnalysis(row)
              if (['review', 'checked'].includes(next[row.review_id].status)) changed = true
            } catch {
              next[row.review_id] = { status: 'unavailable' }
            }
            if (active) {
              statesRef.current = { ...next }
              setStates(statesRef.current)
            }
          }
        }
        await Promise.all([worker(), worker(), worker()])
        if (active) {
          statesRef.current = next
          setStates({ ...next })
          if (changed) readyCallback.current()
        }
      } catch {
        if (active)
          setError('Не удалось обновить статусы разбора. Попробуем ещё раз автоматически.')
      } finally {
        running = false
        if (requested && active) {
          requested = false
          void refresh()
        }
      }
    }
    reloadRef.current = () => {
      statesRef.current = Object.fromEntries(
        Object.entries(statesRef.current).filter(
          ([, state]) => !['failed', 'upload'].includes(state.status),
        ),
      )
      void refresh()
    }
    void refresh()
    const timer = setInterval(() => void refresh(), 10000)
    const onFocus = () => reloadRef.current()
    window.addEventListener('focus', onFocus)
    window.document.addEventListener('visibilitychange', onFocus)
    return () => {
      active = false
      clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      window.document.removeEventListener('visibilitychange', onFocus)
    }
  }, [])
  return { analyses, states, error, refresh, track }
}
